import React from 'react';

import { OpenInNew } from '@mui/icons-material';
import { Box, Chip, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';

import SectionWithAction from './SectionWithAction';

export interface ReferencingResourceCardProps {
	id: string;
	icon: React.ReactNode;
	name: string;
	code: string;
	dependencies: string[];
	isDeployed?: boolean;
	onOpen?: () => void;
	openTitle: string;
}

interface ReferencingResourcesSectionProps {
	title: string;
	emptyMessage: string;
	items: ReferencingResourceCardProps[];
}

export function ReferencingResourcesSection({ title, emptyMessage, items }: ReferencingResourcesSectionProps) {
	return (
		<SectionWithAction title={title} count={items.length} hideAddButton emptyMessage={emptyMessage}>
			<Stack spacing={1}>
				{items.map((item) => (
					<ReferencingResourceCard key={item.id} {...item} />
				))}
			</Stack>
		</SectionWithAction>
	);
}

export default function ReferencingResourceCard({ icon, name, code, dependencies, isDeployed = true, onOpen, openTitle }: ReferencingResourceCardProps) {
	const dependencyCounts = Array.from(
		dependencies.reduce((counts, dependency) => counts.set(dependency, (counts.get(dependency) ?? 0) + 1), new Map<string, number>()),
	).sort(([left], [right]) => left.localeCompare(right));

	return (
		<Paper variant="outlined" sx={{ p: 1, bgcolor: 'background.default' }}>
			<Stack direction="row" spacing={1} alignItems="center">
				<Box sx={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>{icon}</Box>
				<Box sx={{ minWidth: 0, flexGrow: 1 }}>
					<Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
						{name}
					</Typography>
					<Typography variant="caption" color="text.secondary" sx={{ fontFamily: 'monospace' }} noWrap>
						{code}
					</Typography>
				</Box>
				<Stack direction="row" spacing={0.5} alignItems="center" sx={{ flexShrink: 0 }}>
					{!isDeployed && <Chip label="Not deployed" size="small" color="warning" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />}
					{dependencyCounts.map(([dependency, count]) => (
						<Chip key={dependency} label={`${dependency} ${count}`} size="small" variant="outlined" sx={{ height: 20, fontSize: '0.68rem' }} />
					))}
					{onOpen && (
						<Tooltip title={openTitle}>
							<IconButton size="small" onClick={onOpen} sx={{ p: 0.5 }}>
								<OpenInNew sx={{ fontSize: 16 }} />
							</IconButton>
						</Tooltip>
					)}
				</Stack>
			</Stack>
		</Paper>
	);
}
